import { useCallback, useEffect, useRef, useState } from 'react';
import { buildDynamicFilters } from '@/lib/dynamic-filters';
import { SearchValues } from '@/components/custom/erp';
import { useAuth } from '@/contexts/auth-context';
import { invoiceReportColumns } from '../columns';
import { ApiResponse } from '@/types/api.type';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

interface UseSearchLogicProps {
  getReport: (params: BanHangTableParams) => Promise<void>;
  searchParams: BanHangSearchParams;
  showTable: boolean;
  handleInitialSearchHook: (
    formData: BanHangPayload,
    getReport: (params: BanHangTableParams) => Promise<void>
  ) => Promise<void>;
}

export const useSearchLogic = ({
  getReport,
  searchParams,
  showTable,
  handleInitialSearchHook
}: UseSearchLogicProps) => {
  const { entity } = useAuth();

  const [searchState, setSearchState] = useState<{
    filterParams: SearchValues;
    searchResults: BanHangRecord[];
    totalCount: number;
    currentPage: number;
    isSearching: boolean;
  }>({
    filterParams: {},
    searchResults: [],
    totalCount: 0,
    currentPage: 0,
    isSearching: false
  });

  const previousFilterParamsRef = useRef<string>('');
  const isInitialMountRef = useRef(true);
  const filterableFieldsRef = useRef<string[]>(invoiceReportColumns(() => {}).map((col: BanHangRecord) => col.field));

  const buildFiltersFromSearchValues = useCallback((searchValues: SearchValues) => {
    const filterInputs: Record<string, string | undefined> = {};

    filterableFieldsRef.current.forEach(field => {
      const value = searchValues[field];
      if (typeof value === 'string' && value.trim()) {
        filterInputs[field] = value;
      }
    });

    return buildDynamicFilters(filterInputs);
  }, []);

  const handleSearchChange = useCallback((values: SearchValues) => {
    const nextHasFilters = Object.keys(values || {}).length > 0;

    if (!nextHasFilters) {
      setSearchState({
        filterParams: {},
        searchResults: [],
        totalCount: 0,
        currentPage: 0,
        isSearching: false
      });
      return;
    }

    setSearchState(prev => ({
      ...prev,
      filterParams: values,
      isSearching: false,
      searchResults: [],
      currentPage: 0
    }));
  }, []);

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      previousFilterParamsRef.current = JSON.stringify(searchState.filterParams);
      return;
    }

    if (!searchState.filterParams || Object.keys(searchState.filterParams).length === 0) {
      return;
    }

    const currentFilterParamsStr = JSON.stringify(searchState.filterParams);
    const hasChanged = previousFilterParamsRef.current !== currentFilterParamsStr;

    if (!hasChanged || !showTable || !searchParams || !entity?.slug) {
      return;
    }

    const fetchFilteredData = async () => {
      setSearchState(prev => ({ ...prev, isSearching: true }));

      try {
        const dynamicFilters = buildFiltersFromSearchValues(searchState.filterParams);

        const requestData = {
          ...searchParams,
          ...(Object.keys(dynamicFilters).length > 0 && { dynamic_filters: dynamicFilters })
        };

        const params = {
          page: searchState.currentPage + 1,
          page_size: 30
        };

        const response = await api.post<ApiResponse<BanHangRecord>>(
          `/entities/${entity.slug}/erp/${QUERY_KEYS.BANG_TONG_HOP_HOA_DON_BAN_HANG}/`,
          requestData,
          { params }
        );

        previousFilterParamsRef.current = currentFilterParamsStr;
        setSearchState(prev => ({
          ...prev,
          searchResults: response.data.results || response.data || [],
          totalCount: response.data.count || 0,
          isSearching: false
        }));
      } catch (error) {
        console.error('Error fetching filtered data:', error);
        setSearchState(prev => ({
          ...prev,
          searchResults: [],
          totalCount: 0,
          isSearching: false
        }));
      }
    };

    fetchFilteredData();
  }, [
    searchState.filterParams,
    searchState.currentPage,
    showTable,
    searchParams,
    entity?.slug,
    buildFiltersFromSearchValues
  ]);

  const handleSearchPageChange = useCallback((page: number) => {
    setSearchState(prev => ({
      ...prev,
      currentPage: page
    }));
  }, []);

  const hasFilterParams = searchState.filterParams && Object.keys(searchState.filterParams).length > 0;

  const handleInitialSearch = useCallback(
    async (formData: BanHangPayload) => {
      const dynamicFilters = buildFiltersFromSearchValues(searchState.filterParams);
      const requestData = {
        ...formData,
        ...(Object.keys(dynamicFilters).length > 0 && { dynamic_filters: dynamicFilters })
      };
      await handleInitialSearchHook(requestData, getReport);
    },
    [searchState.filterParams, buildFiltersFromSearchValues, handleInitialSearchHook, getReport]
  );

  const handleRefreshClick = useCallback(async () => {
    if (hasFilterParams) {
      setSearchState(prev => ({
        ...prev,
        searchResults: [],
        currentPage: 0
      }));
    } else {
      await getReport(searchParams);
    }
  }, [hasFilterParams, searchParams, getReport]);

  return {
    searchState,
    handleSearchChange,
    handleSearchPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick,
    isSearching: searchState.isSearching
  };
};
